import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import Card from '../../../ui/Card';
import { ICONS } from '../../../ui/icon-map';
import Stat, { StatGrid } from '../../../ui/Stat/Stat';
import EmptyState from '../../../ui/EmptyState';
import DrillDownModal from '../components/DrillDownModal';
import ShareRow from '../components/ShareRow';
import { opsSummary, dailySeries, openedIn } from '../kpi.logic';

// The branch's work in the period, read against itself: per day, the previous period of the same
// length, and the average working day. No fixed targets.
const OperationsDashboard = ({ allOps = [], filters }) => {
    const { hasPermission } = usePermissions();
    const canDrillDown = hasPermission(PERMISSIONS.view_kpi_drilldown);
    const [modal, setModal] = useState({ open: false, title: '', data: [] });
    const open = (title, data) => canDrillDown && setModal({ open: true, title, data });

    const f = useMemo(() => ({ from: filters.startDate, to: filters.endDate, branch: filters.branch }),
        [filters.startDate, filters.endDate, filters.branch]);
    const stats = useMemo(() => opsSummary(allOps, f), [allOps, f]);
    const trend = useMemo(() => dailySeries(allOps, f), [allOps, f]);
    const max = Math.max(1, ...trend.series.map(([, c]) => c));
    const barColor = (c) => (c === 0 ? 'var(--surface-2)' : c >= trend.avg ? 'var(--success)' : c >= trend.avg * 0.7 ? 'var(--warning)' : 'var(--danger)');
    const changeText = stats.change == null ? 'No contracts in the previous period' : `${stats.change > 0 ? '+' : ''}${stats.change}% vs the previous ${stats.days} days (${stats.prevOpened})`;

    return (
        <div className="flex flex-col gap-6">

            <StatGrid min={200}>
                <Stat
                    label="Contracts opened"
                    value={stats.opened.length}
                    icon={ICONS.operations}
                    meta={`${stats.perDay} per day over ${stats.days} days`}
                    onClick={canDrillDown ? () => open('Contracts opened in the period', stats.opened) : undefined}
                />
                <Stat
                    label="Contracts closed"
                    value={stats.closed.length}
                    icon={ICONS.contracts}
                    meta="Cars returned in the period"
                    onClick={canDrillDown ? () => open('Contracts closed in the period', stats.closed) : undefined}
                />
                <Stat
                    label="On the road"
                    value={stats.active.length}
                    icon={ICONS.activity}
                    meta={`Open contracts on ${filters.endDate}`}
                    onClick={canDrillDown ? () => open(`On the road on ${filters.endDate}`, stats.active) : undefined}
                />
                <Stat
                    label="vs previous period"
                    value={stats.change == null ? '—' : `${stats.change > 0 ? '+' : ''}${stats.change}%`}
                    icon={ICONS.kpi}
                    tone={stats.change == null ? undefined : stats.change >= 0 ? 'success' : 'danger'}
                    meta={changeText}
                />
            </StatGrid>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <Card
                    title="Contracts per day"
                    subtitle={`${trend.from} to ${trend.to}. The dashed line is the average working day (${trend.avg}); green at or above it, amber within 70%, red below.`}
                >
                    <div className="h-[250px] flex items-end gap-1 pl-14 pr-2 border-b border-l border-gray-200">
                        <div className="flex-1 h-full flex items-end gap-1 relative">
                            {trend.avg > 0 && (
                                <div
                                    className="absolute w-full border-t border-dashed z-10 flex items-center pointer-events-none"
                                    style={{ bottom: `${(trend.avg / max) * 100}%`, borderColor: 'var(--accent)' }}
                                >
                                    <span className="absolute -left-14 text-[11px] font-medium text-purple-800 bg-white px-1 rounded">Avg {trend.avg}</span>
                                </div>
                            )}
                            {trend.series.map(([day, count]) => (
                                <button
                                    type="button"
                                    key={day}
                                    aria-label={`${day}: ${count} contracts`}
                                    className="flex-1 rounded-t group relative min-w-[4px] mx-px cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
                                    style={{ height: `${Math.max((count / max) * 100, 2)}%`, backgroundColor: barColor(count), zIndex: 20 }}
                                    onClick={() => open(`Contracts on ${day}`, openedIn(allOps, { from: day, to: day, branch: filters.branch }))}
                                >
                                    <span className="hidden group-hover:flex group-focus-visible:flex flex-col items-center absolute -top-14 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded-md z-[100] whitespace-nowrap pointer-events-none">
                                        <span className="text-[11px] text-gray-300">{day}</span>
                                        <span className="text-xs font-semibold">{count} contracts</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-3 flex justify-between text-xs text-gray-500 px-2">
                        <span>{trend.from}</span>
                        <span>{trend.to}</span>
                    </div>
                </Card>

                <Card title="Contract type" subtitle="Contracts opened in the period">
                    <div className="space-y-1">
                        {stats.byType.map(([type, count]) => (
                            <ShareRow
                                key={type}
                                label={type}
                                value={`(${count})`}
                                percent={Math.round((count / stats.opened.length) * 100)}
                                barWidth={Math.round((count / stats.opened.length) * 100)}
                                onClick={() => open(`${type} contracts`, stats.opened.filter((i) => (i.type || 'Daily') === type))}
                            />
                        ))}
                        {stats.byType.length === 0 && (
                            <EmptyState icon={ICONS.contracts} title="No contracts opened in this period" />
                        )}
                    </div>
                </Card>

                <Card title="Branch distribution" subtitle="Contracts opened in the period">
                    <div className="space-y-1 max-h-[250px] overflow-y-auto pr-1">
                        {stats.byBranch.map(([branch, count]) => {
                            const pct = Math.round((count / stats.opened.length) * 100);
                            return (
                                <ShareRow
                                    key={branch}
                                    label={branch}
                                    value={`(${count})`}
                                    percent={pct}
                                    barWidth={pct}
                                    onClick={() => open(`Contracts - ${branch}`, stats.opened.filter((i) => i.branch === branch))}
                                />
                            );
                        })}
                        {stats.byBranch.length === 0 && <EmptyState icon={ICONS.home} title="No data available" />}
                    </div>
                </Card>

            </div>

            <DrillDownModal
                isOpen={modal.open}
                onClose={() => setModal({ ...modal, open: false })}
                title={modal.title}
                data={modal.data}
            />
        </div>
    );
};

export default OperationsDashboard;
